import type { EvalClusterDocument } from "./evalPlaygroundStorage";

/** Prompt sizing of `phase3_synthesis`: MAX_EVIDENCE_CHARS_PER_SOURCE / _PER_PROMPT. */
const SEGMENT_CHARS = 12_000;
const PROMPT_CHARS = 48_000;

export type EvalClusterPlan = {
  calls: number;
  batches: number;
  unread: number;
  partial: number;
  chars: number;
};

/**
 * Mirrors `_split_evidence_rows_for_prompt` + `_partition_evidence_rows_for_prompt` (same section
 * header, same greedy packing) so the call count is known before paying for it. The run's own
 * coverage report stays the authority.
 */
export function estimateEvalPlan(
  docs: EvalClusterDocument[],
  contractor: string,
  maxCalls: number,
): EvalClusterPlan {
  const sections: { doc: string; size: number }[] = [];
  for (const d of docs) {
    const count = Math.max(1, Math.ceil(d.chars / SEGMENT_CHARS));
    const header = `---\nTitle: ${d.title}\nURL: ${d.url}\nMain contractor (extracted): ${contractor}\n\n`
      .length;
    for (let i = 0; i < count; i++) {
      const note =
        count > 1 ? `\nEvidence segment ${i + 1} of ${count}; read it as part of the same URL.`.length : 0;
      const body = Math.max(0, Math.min(SEGMENT_CHARS, d.chars - i * SEGMENT_CHARS));
      sections.push({ doc: d.evidence_id, size: header + note + body + 1 });
    }
  }
  const batchOf: number[] = [];
  let batch = 0;
  let used = 0;
  sections.forEach((s, i) => {
    if (i > 0 && used + s.size > PROMPT_CHARS) {
      batch += 1;
      used = 0;
    }
    used += s.size;
    batchOf.push(batch);
  });
  const batches = sections.length ? batch + 1 : 0;
  const perDoc = new Map<string, { read: number; total: number }>();
  sections.forEach((s, i) => {
    const entry = perDoc.get(s.doc) ?? { read: 0, total: 0 };
    entry.total += 1;
    if (batchOf[i] < maxCalls) entry.read += 1;
    perDoc.set(s.doc, entry);
  });
  let unread = 0;
  let partial = 0;
  for (const { read, total } of perDoc.values()) {
    if (read === 0) unread += 1;
    else if (read < total) partial += 1;
  }
  return {
    calls: Math.min(batches, maxCalls),
    batches,
    unread,
    partial,
    chars: docs.reduce((sum, d) => sum + d.chars, 0),
  };
}

export function formatChars(chars: number): string {
  return chars >= 1000 ? `${(chars / 1000).toFixed(chars >= 100_000 ? 0 : 1)}k` : String(chars);
}
